/* Copyright (c) 2017 Backpack Health LLC */
/* global window */

import React from 'react';
import { connect } from 'react-redux';
import { injectIntl } from 'react-intl';

import PasscodeEntry from '../PasscodeEntry/PasscodeEntry';
import ShareCardService from '../../../../apiServices/ShareCardService';
import { publicPasscode, publicHash } from '../../../../utilities/LocalStorage';
import AppUtils from '../../../../utilities/AppUtils';
import ManageSession from '../../../../utilities/ManageSession';
import Inactive from '../PasscodeEntry/Inactive';
import Invalid from '../PasscodeEntry/Invalid';

interface State {
  isNotFound: boolean;
  isFileNotFound: boolean;
  isUnauthorized: boolean;
  isLoading: boolean;
  isInactive: boolean;
  isUnauthorizedAccessCodeUrl: boolean;
}

interface Props {
  intl: any;
  hash: string;
  fileId: string;
  requiresPasscode: boolean;
  vendorName?: string;
  accessCode?: string;
}

class PublicShareCardFileController extends React.Component<Props, State> {

  state: State = {
    isLoading: true,
    isNotFound: false,
    isFileNotFound: false,
    isUnauthorized: false,
    isInactive: false,
    isUnauthorizedAccessCodeUrl: false,
  };

  UNSAFE_componentWillMount() {
    const existingHash = publicHash.get();
    if (existingHash !== this.props.hash) {
      publicHash.set(this.props.hash);
      publicPasscode.remove();
    }

    const savedPasscode = publicPasscode.get() || '';

    if (this.props.requiresPasscode && this.props.accessCode) {
      const encodedPassCode = window.btoa(`:${this.props.accessCode}`);
      this.requestShareCard(encodedPassCode, (apiError, url) => {
        this.receiveFileUrl(url);
      });
    } else if (!this.props.requiresPasscode || savedPasscode) {
      this.requestShareCard(savedPasscode, (apiError, url) => {
        this.receiveFileUrl(url);
        this.startIdleTimer();
      });
    } else {
      this.setState({
        isLoading: false,
        isUnauthorized: true,
      });
    }
  }

  requestShareCard = (passcode: string, callback) => {
    const { hash, fileId, accessCode, intl } = this.props;
    const languageCode = intl.locale;

    ShareCardService.getPublicMetadata(hash, passcode, languageCode, (apiError, res) => {
      if (apiError) {
        this.setState({
          isNotFound: apiError.isNotFound(),
          isFileNotFound: false,
          isUnauthorized: apiError.isUnauthorized(),
          isLoading: false,
          isUnauthorizedAccessCodeUrl: apiError.isUnauthorized() && !!accessCode,
        });
        callback(apiError, '');
        return;
      }

      ShareCardService.getPublicFilePreviewUrl(hash, fileId, passcode, (apiError2, url) => {
        if (apiError2) {
          this.setState({
            isNotFound: false,
            isFileNotFound: apiError2.isNotFound(),
            isUnauthorized: apiError2.isUnauthorized(),
            isLoading: false,
          });
          return;
        }
        callback(apiError2, url);
      });
    });
  }

  startIdleTimer = () => {
    let cancelIdleTimer;
    cancelIdleTimer = ManageSession.startPublicIdleTimer(() => {
      AppUtils.clearSavedData();
      cancelIdleTimer();
      this.setState({
        isInactive: true,
        isUnauthorized: true,
        isNotFound: false,
      });
    });
  }

  receiveFileUrl = (url) => {
    this.setState({
      isFileNotFound: false,
      isNotFound: false,
      isUnauthorized: false,
      isLoading: false,
    });

    window.location.href = url;
  }

  handleNotFoundEvent = () => {
    this.setState({
      isNotFound: false,
      isFileNotFound: true,
      isLoading: false,
    });
  }

  render() {
    if (this.state.isInactive) { return <Inactive onRetry={() => { this.setState({ isNotFound: false, isUnauthorized: true, isInactive: false }); }} />; }
    if (this.state.isLoading) { return null; }
    if (this.state.isNotFound) { return <Invalid hash={this.props.hash} />; }
    if (this.state.isUnauthorizedAccessCodeUrl) { return <Invalid />; }
    if (this.state.isFileNotFound) {
      return (
        <Invalid
          pageTitlePhraseId="shareCardURLInvalid.stickyj.page.title"
          titlePhraseId="shareCardURLInvalid.stickyj.title"
          messagePhraseId="shareCardURLInvalid.file.message.text"
          hash={this.props.hash}
          contactVendor={this.props.vendorName}
        />
      );
    }

    return (
      this.state.isUnauthorized ? (
        <PasscodeEntry
          hash={this.props.hash}
          onSubmit={this.requestShareCard}
          onSuccess={this.receiveFileUrl}
          onNotFound={this.handleNotFoundEvent}
        />
      ) : null
    );
  }
}

function mapPasscodeStateToProps(state, routerProps) {
  const { hash, fileId } = routerProps.match.params;
  const searchParams = AppUtils.getQueryParams(routerProps.location.search);

  return {
    hash,
    accessCode: searchParams.access_code,
    requiresPasscode: true,
    fileId,
  };
}
export const PasscodeAccessiblePublicShareCardFile = connect(mapPasscodeStateToProps)(injectIntl(PublicShareCardFileController));

function mapJewelryStateToProps(state, routerProps) {
  const { vendorName, hash, fileId } = routerProps.match.params;

  return {
    hash: `${vendorName}/${hash}`,
    vendorName,
    requiresPasscode: false,
    fileId,
  };
}
export const JewelryAccessiblePublicShareCardFile = connect(mapJewelryStateToProps)(injectIntl(PublicShareCardFileController));

export default PublicShareCardFileController;
